import {BaseComponent} from '../base/component.base.js';
import {formatEuro} from '../../core/billing/billing.core.js';
import {reportChart} from '../../core/billing/charts.core.js';
import {BILLING_CHART_MODE} from '../../core/vocabulary.js';
import template from './billing-charts.template.html';
import styles from './billing-charts.styles.css';

export class BillingChartsComponent extends BaseComponent {
    #years;
    #oneOffByYear;
    #selectedYear;
    #width;
    #observer = new ResizeObserver(() => {
        const width = this.$('.chart-body').clientWidth;
        if (width !== this.#width) this.#draw();
    });

    connectedCallback() {
        this.loadTemplate(template, styles);
        this.$('#chart-mode').addEventListener('change', () => this.#draw());
        this.shadowRoot.addEventListener('pointermove', (e) => this.#showTip(e));
        this.addEventListener('pointerleave', () => this.#hideTip());
        this.shadowRoot.addEventListener('click', (e) => this.#select(e));
        this.shadowRoot.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                if (e.target.closest?.('[data-year]')) { e.preventDefault(); this.#select(e); }
            }
        });
        this.#observer.observe(this.$('.chart-body'));
    }

    disconnectedCallback() { this.#observer.disconnect(); }

    /** @param {Map<number, number>} [oneOffByYear] one-off income per year, named apart in each year's tooltip */
    showYears(years, selectedYear, oneOffByYear = new Map()) {
        this.#years = years;
        this.#oneOffByYear = oneOffByYear;
        this.#selectedYear = selectedYear;
        this.#draw();
    }

    showUnavailable() {
        this.#years = undefined;
        this.#hideTip();
        this.$('.chart-body').innerHTML = '<p class="unavailable">Not available right now.</p>';
    }

    #select(event) {
        const year = event.target.closest?.('[data-year]')?.dataset.year;
        if (year !== undefined) this.emit('billing-year-select', {year: Number(year)});
    }

    #draw() {
        if (!this.#years) return;
        const body = this.$('.chart-body');
        const focusedYear = this.shadowRoot.activeElement?.dataset.year;
        this.#width = body.clientWidth;
        this.#hideTip();
        body.innerHTML = this.#years.length ? reportChart(this.#years,
            this.$('#chart-mode').value || BILLING_CHART_MODE.NET, this.#selectedYear, this.#width || 480, this.#oneOffByYear)
            : '<p class="empty">No recorded invoices or costs yet.</p>';
        if (focusedYear) body.querySelector(`[data-year="${focusedYear}"]`)?.focus({preventScroll: true});
    }

    /** Shows the tooltip of the column or row under the pointer, built with `textContent` from its data. */
    #showTip(event) {
        const mark = event.target.closest?.('.mark');
        if (!mark) {
            this.#hideTip();
            return;
        }
        const tip = this.$('.tooltip');
        const title = document.createElement('p');
        title.className = 'tooltip-title';
        title.textContent = mark.dataset.tipTitle;
        const rows = JSON.parse(mark.dataset.tipRows).map(([label, value]) => {
            const row = document.createElement('p');
            row.className = 'tooltip-row';
            const name = document.createElement('span');
            name.textContent = label;
            const figure = document.createElement('strong');
            figure.textContent = formatEuro(value);
            row.append(name, figure);
            return row;
        });
        tip.replaceChildren(title, ...rows);
        tip.hidden = false;
        const host = this.getBoundingClientRect();
        const x = event.clientX - host.left;
        const flip = x + 16 + tip.offsetWidth > host.width;
        tip.style.left = `${Math.max(0, flip ? x - 16 - tip.offsetWidth : x + 16)}px`;
        tip.style.top = `${Math.max(0, Math.min(event.clientY - host.top + 16, host.height - tip.offsetHeight))}px`;
    }

    #hideTip() {
        this.$('.tooltip').hidden = true;
    }
}
customElements.define('billing-charts', BillingChartsComponent);
